import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { Form, Row, Col, Table } from "react-bootstrap";
import { updateDb } from "../services/updateDb";
import { useAppContext } from "../AppContext";
import { useTranslation } from "react-i18next";
import useAxiosFetch from "../common/useAxiosFetch";
import { getScoreBorderClass } from "../common/helpers";

function ReviewRiskModal({ riskObj, show, setRefreshData }) {
  const { t } = useTranslation();
  const [reviewDecision, setReviewDecision] = useState("");
  const [reviewReason, setReviewReason] = useState("");
  const { currentOrg, showAlertMessage, closeModal } = useAppContext();

  // Always prefer the freshest risk from the API so reviewedByUser is populated
  const { data: freshRisk, refetch: refetchRisk } = useAxiosFetch(`/api/risks/${riskObj?.id}`);
  const displayRisk = freshRisk || riskObj;

  useEffect(() => {
    // Reset form when modal is shown or when riskObj changes
    if (show) {
      setReviewDecision("");
      setReviewReason("");
      
      // Reset form element
      const form = document.getElementById("reviewRisk");
      if (form) {
        form.reset();
      }

      // Pull the latest risk details when opening
      if (riskObj?.id) {
        refetchRisk?.();
      }
    }
  }, [show, riskObj]);

  const handleModalClose = async (reviewData) => {
    if (reviewData) {
      const res = await updateDb("/api/risks/review/" + reviewData.id, {
        riskApproved: reviewData.riskApproved,
        reviewReason: reviewData.reviewReason
      });

      if (res.success) {
        setRefreshData(new Date().getTime());
        const action = reviewData.riskApproved ? t('approved') : t('rejected');
        showAlertMessage('success', t('risk') + " " + action);
      } else {
        showAlertMessage('warning', res?.error);
      }
    }

    closeModal();
  };

  const getRiskApprovalStatus = () => {
    if (displayRisk.riskApproved === true) return 'approved';
    if (displayRisk.riskApproved === false) return 'rejected';
    return 'pending';
  };

  const getApprovalStatusColor = () => {
    const status = getRiskApprovalStatus();
    switch (status) {
      case 'approved': return 'var(--green-risk-score)';
      case 'rejected': return 'var(--red-risk-score)';
      default: return 'var(--yellow-risk-score)';
    }
  };

  if (riskObj && riskObj.id) {
    return (
      <Modal show={show} onHide={closeModal} size="lg">
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>{t('review_risk')}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="update-score-modal">
          <Form id="reviewRisk">
            
            <Row className="mb-4">
              <Col>
                <div className="risk-description-section" style={{
                  padding: '20px 0px',
                  marginBottom: '10px'
                }}>
                  <h5 style={{ 
                    color: 'var(--text-color)', 
                    marginBottom: '15px',
                    fontWeight: 'bold'
                  }}>
                    {t('risk_description')}
                  </h5>
                  <div style={{ 
                    fontSize: '1.1em',
                    lineHeight: '1.5'
                  }}>
                    {displayRisk.description}
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col><b>{t('current_status')}:</b></Col>
              <Col>
                <span style={{ 
                  color: getApprovalStatusColor(), 
                  fontWeight: 'bold',
                  textTransform: 'capitalize'
                }}>
                  {t(getRiskApprovalStatus())}
                </span>
                {displayRisk.riskReviewedBy && (
                  <div className="small-text">
                    {t('reviewed_by')}: {displayRisk.riskReviewedByUser || displayRisk.riskReviewedBy}
                    {displayRisk.riskReviewedAt && (
                      <> {t('on')} {new Date(displayRisk.riskReviewedAt).toLocaleDateString()}</>
                    )}
                  </div>
                )}
              </Col>
            </Row>

            <Row className="mb-3">
              <Col><b>{t('current_risk_score')}:</b></Col>
              <Col>
                <div className={getScoreBorderClass(displayRisk["actual-chance"] * displayRisk['actual-impact'], currentOrg.rangeBounds)}
                  style={{ display: 'inline-block', textAlign: 'center' }}
                >
                  {displayRisk["actual-chance"] * displayRisk['actual-impact']}
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col><b>{t('target_score')}:</b></Col>
              <Col>
                <div className={getScoreBorderClass(displayRisk["expected-chance"] * displayRisk['expected-impact'], currentOrg.rangeBounds)}
                  style={{ display: 'inline-block', textAlign: 'center' }}
                >
                  {displayRisk["expected-chance"] * displayRisk['expected-impact']}
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col><b>{t('owner')}:</b></Col>
              <Col>{displayRisk.owner}</Col>
            </Row>

            <Row className="mb-3">
              <Col><b>{t('treatment')}:</b></Col>
              <Col>
                {displayRisk.treatment ? t(displayRisk.treatment) : t('not_set')}
                {displayRisk.treatment === 'accept' && displayRisk.tempAcceptTreatment && displayRisk.acceptTreatmentDate && (
                  <div className="small-text" style={{ marginTop: '5px' }}>
                    {t('temporarily_accept')} {t('until')} {new Date(displayRisk.acceptTreatmentDate).toLocaleDateString()}
                  </div>
                )}
              </Col>
            </Row>

            {displayRisk.measures && displayRisk.measures.length > 0 && (
              <Row className="mb-3">
                <Col>
                  <div className="measures-section">
                    <h6 style={{ 
                      fontWeight: 'bold', 
                      marginBottom: '15px'
                    }}>
                      {t('measures')} ({displayRisk.measures.length})
                    </h6>
                    <div style={{

                    }}>
                      <table className="table-details" style={{ fontSize: '0.95em', borderWidth: '1px'}}>
                        <thead >
                          <tr>
                            <th style={{ width: '60px', textAlign: 'center' }}>#</th>
                            <th>{t('measure')}</th>
                            <th style={{ width: '120px', textAlign: 'center' }}>{t('status')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayRisk.measures.map((measure, index) => (
                            <tr key={index}>
                              <td style={{ fontWeight: 'bold' }}>
                                {index + 1}
                              </td>
                              <td style={{ lineHeight: '1.4', textAlign: 'left' }}>
                                {measure.measure}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                {measure.status ? (
                                  <span className={`badge ${
                                    measure.status === 'completed' ? 'bg-success' :
                                    measure.status === 'in_progress' ? 'bg-warning' :
                                    measure.status === 'planned' ? 'bg-info' :
                                    'bg-secondary'
                                  }`}>
                                    {t(measure.status)}
                                  </span>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </Col>
              </Row>
            )}

            <hr />

            <Row className="mb-3">
              <Form.Group as={Col} controlId="reviewDecision">
                <Form.Label><b>{t('review_decision')}:</b></Form.Label>
                <Form.Select 
                  name="reviewDecision" 
                  value={reviewDecision}
                  onChange={(e) => setReviewDecision(e.target.value)}
                  required
                >
                  <option value="">{t('select_decision')}</option>
                  <option value="approve">{t('approve')}</option>
                  <option value="reject">{t('reject')}</option>
                </Form.Select>
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} controlId="reviewReason">
                <Form.Label>{t('review_reason')} ({t('optional').toLowerCase()}):</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="reviewReason"
                  value={reviewReason}
                  onChange={(e) => setReviewReason(e.target.value)}
                  placeholder={t('review_reason_placeholder')}
                />
              </Form.Group>
            </Row>

          </Form>
        </Modal.Body>

        <Modal.Footer>
          <div className="white-border-btn cancel-btn" onClick={closeModal}>
            {t('cancel')}
          </div>

          <div style={{ flexGrow: 1 }} />

          <div
            className="white-border-btn submit-btn"
            onClick={() => {
              if (!reviewDecision) {
                showAlertMessage('warning', t('please_select_decision'));
                return;
              }
              
              handleModalClose({
                id: riskObj.id,
                riskApproved: reviewDecision === 'approve',
                reviewReason: reviewReason
              });
            }}
          >
            {t('save')}
          </div>
        </Modal.Footer>
      </Modal>
    );
  }

  return null;
}

export default ReviewRiskModal;
